import type { Maybe } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import { cancelTermin, getOtherInspectorNameForTermin } from '@ils/evaluation-scheduler/api/EvaluationTermin.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import { filter, switchMap } from 'rxjs';
import type { EvaluationTermin } from '../db/EvaluationTermin.js';
import './ils-countdown.js';

@customElement('ils-evaluation-termin-card')
export class ILSEvaluationTerminCard extends ChemistryLitElement {
  @property({ attribute: false }) termin = new RXController<Maybe<EvaluationTermin>>(this, undefined);

  @state() private readonly _otherInspectorName = new RXController(
    this,
    this.termin.observable.pipe(
      filter(Boolean),
      switchMap(termin => getOtherInspectorNameForTermin(termin._id)()),
    ),
  );

  override render() {
    const termin = this.termin.value;
    const otherInspectorName = this._otherInspectorName.value;

    if (!termin || !otherInspectorName) return nothing;

    const isInFuture = termin.start > DateTime.now();

    return html`
      <x-flex
        space="md"
        space-between
        ${css({ background: '#fff', padding: `${this.spacing.lg} ${this.spacing.md}`, borderRadius: '8px' })}
      >
        <x-flex space="sm">
          <x-text> ${otherInspectorName} </x-text>
          <x-text> am ${termin.start.setLocale('de-DE').toLocaleString(DateTime.DATETIME_MED)} </x-text>
          <x-text ${css({ fontStyle: 'italic' })}>
            Dauer: ${this._millisToMinutes(termin.end.toMillis() - termin.start.toMillis())} Minuten
          </x-text>
        </x-flex>
        <x-hr></x-hr>
        ${isInFuture
          ? html`
              <x-flex horizontal space-between crossaxis-center space="sm" wrap>
                <x-h4> Teilnahme in </x-h4>
                <ils-countdown
                  .datetime=${termin.start}
                  @timer-exceeded=${() => {
                    this.requestUpdate();
                  }}
                  ${css({ alignSelf: 'end' })}
                ></ils-countdown>
              </x-flex>
            `
          : html`
              <x-button href="/start/evaluation-termin/${termin._id}">
                <x-h4> Jetzt teilnehmen </x-h4>
              </x-button>
            `}
        <x-hr></x-hr>
        <x-button
          tone="error"
          mode="outline"
          icon="event_busy"
          ${css({ alignSelf: 'end' })}
          @click=${() =>
            XNativeDialog.guard(() => cancelTermin(termin._id)(), {
              confirmText: 'Willst du diesen Termin wirklich absagen?',
              hasToConfirm: true,
              showLoaderModal: true,
              errorMessagePrefix: 'Fehler beim Absagen des Termins',
            })}
        >
          Absagen
        </x-button>
      </x-flex>
    `;
  }

  private _millisToMinutes(millis: number) {
    const seconds = millis / 1000;
    const minutes = seconds / 60;
    return minutes;
  }
}
